import { DumbbellIcon } from "@/assets/images/svgIcons/blocks/dumbbellIcon"
import { Button } from "@/components/ui/baseUi/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { useCalendar } from "@/modules/calendar/hooks/useCalendar"
import { word } from "@/modules/core/utils/i18n"
import { cn } from "@/modules/lib/utils"
import { useFlipActionsPanel } from "@/modules/programs/hooks/useFlipActionsPanel"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import type { TemplateSessionEntity } from "@/modules/template/entities/templateElementEntity"
import { EyeIcon, LightbulbIcon, NotebookPenIcon, VideoIcon } from "lucide-react"

export function TemplateCalendarSessionHeaderContainer({
  children,
  canSeeActions,
  onCreateBlockClick,
  onSeeSessionClick,
  session,
  className,
  ...props
}: {
  children: React.ReactNode
  canSeeActions: boolean
  onCreateBlockClick: (session: TemplateSessionEntity, newBlockType: SessionBlockType) => void
  onSeeSessionClick: () => void
  session: TemplateSessionEntity
} & React.ComponentProps<"div">) {
  const { isDragging } = useCalendar()
  const { containerRef, shouldFlipToLeft, checkAvailableSpace } = useFlipActionsPanel()

  const handleCreateBlockClick = (e: React.MouseEvent<HTMLButtonElement>, newBlockType: SessionBlockType) => {
    if (!canSeeActions) return
    e.stopPropagation()

    onCreateBlockClick(session, newBlockType)
  }

  return (
    <div
      ref={containerRef}
      id="session-header"
      className={cn("group relative flex w-full flex-row items-start", className)}
      onMouseEnter={checkAvailableSpace}
      {...props}
    >
      <div className="gap-2 p-1">{children}</div>

      {canSeeActions && !isDragging && (
        <div
          className={cn(
            "absolute z-3 hidden animate-(--animate-flip) flex-row py-1 group-hover:flex",
            shouldFlipToLeft && "flex-row-reverse",
          )}
          style={shouldFlipToLeft ? { left: "-2.7rem" } : { right: "-2.7rem" }}
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          {/* Empty space to avoid the hover to be lost between the block and the actions */}
          <div className="h-full w-2" />
          <div className="w-2rem flex flex-col items-center justify-center gap-4 rounded-2xl bg-primary-100 p-1">
            <ActionButton
              tooltip={word("program.session.see_details")}
              onClick={onSeeSessionClick}
              tooltipSide={shouldFlipToLeft ? "left" : "right"}
            >
              <EyeIcon size={16} color="currentColor" />
            </ActionButton>

            <div className="flex flex-col gap-2">
              <ActionButton
                tooltip={word("program.session.block.exercise.add")}
                onClick={(e) => {
                  handleCreateBlockClick(e, SessionBlockType.SESSION_EXERCISE)
                }}
                tooltipSide={shouldFlipToLeft ? "left" : "right"}
              >
                <DumbbellIcon size={16} color="currentColor" />
              </ActionButton>

              <ActionButton
                tooltip={word("program.session.block.media.add")}
                onClick={(e) => {
                  handleCreateBlockClick(e, SessionBlockType.MEDIAS)
                }}
                tooltipSide={shouldFlipToLeft ? "left" : "right"}
              >
                <VideoIcon size={16} color="currentColor" />
              </ActionButton>

              <ActionButton
                tooltip={word("program.session.block.note_for_coaches.add")}
                onClick={(e) => {
                  handleCreateBlockClick(e, SessionBlockType.COACH_NOTE)
                }}
                tooltipSide={shouldFlipToLeft ? "left" : "right"}
              >
                <LightbulbIcon size={16} color="currentColor" />
              </ActionButton>

              <ActionButton
                tooltip={word("program.session.block.note_for_athletes.add")}
                onClick={(e) => {
                  handleCreateBlockClick(e, SessionBlockType.ATHLETE_NOTE)
                }}
                tooltipSide={shouldFlipToLeft ? "left" : "right"}
              >
                <NotebookPenIcon size={16} color="currentColor" />
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ActionButton({
  children,
  tooltip,
  onClick,
  buttonClassName,
  tooltipSide = "right",
  ...buttonProps
}: React.ComponentProps<typeof Button> & {
  children: React.ReactNode
  tooltip: string
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  buttonClassName?: string
  tooltipSide?: "left" | "right"
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            size="iconXs"
            variant="secondary"
            onClick={onClick}
            className={cn("transition-colors duration-200 hover:bg-neutral-100", buttonClassName)}
            {...buttonProps}
          >
            {children}
          </Button>
        }
      />
      <TooltipContent side={tooltipSide} align="center">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  )
}
